"use client";

import * as React from "react";
import { useFormContext, useWatch } from 'react-hook-form';
import { ChevronDownIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useFormModeGuard } from "@/forms/shared/components/use-form-mode-guard";

interface DateTimeFieldProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  dateOnly?: boolean;
}

const parseISOStringLocal = (isoString: string): Date => {
  const [datePart, timePart] = isoString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = (timePart || '00:00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds || 0);
};

const formatToISOLocal = (date: Date, includeTime: boolean = true): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  if (!includeTime) {
    return `${year}-${month}-${day}`;
  }
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

interface ResolvedDateTimeValue {
  date: Date | undefined;
  time: string;
}

const resolveDateTimeValue = (value: string | null | undefined): ResolvedDateTimeValue => {
  if (!value) {
    return { date: undefined, time: "00:00" };
  }

  try {
    const parsedDate = value.includes("T")
      ? parseISOStringLocal(value)
      : parseISOStringLocal(`${value}T00:00:00`);

    if (Number.isNaN(parsedDate.getTime())) {
      return { date: undefined, time: "00:00" };
    }

    const hours = parsedDate.getHours().toString().padStart(2, "0");
    const minutes = parsedDate.getMinutes().toString().padStart(2, "0");
    return { date: parsedDate, time: `${hours}:${minutes}` };
  } catch {
    return { date: undefined, time: "00:00" };
  }
};

export function DateTimeField({
  name,
  label,
  required = false,
  disabled = false,
  placeholder = "dd/mm/aaaa",
  dateOnly = false,
}: DateTimeFieldProps) {
  const form = useFormContext();
  const { sideEffectsAllowed } = useFormModeGuard(disabled);
  const [isOpen, setIsOpen] = React.useState(false);

  const watchedValue = useWatch({ control: form.control, name });
  const { date, time } = resolveDateTimeValue(watchedValue);

  const handleDateTimeChange = (newDate?: Date, newTime?: string) => {
    if (!sideEffectsAllowed) return;
    const currentDate = newDate !== undefined ? newDate : date;
    const currentTime = newTime !== undefined ? newTime : time;

    if (currentDate) {
      if (dateOnly) {
        const dateWithMidnight = new Date(currentDate);
        dateWithMidnight.setHours(0, 0, 0, 0);
        const isoString = formatToISOLocal(dateWithMidnight, true);
        form.setValue(name, isoString);
      } else if (currentTime) {
        const [hours, minutes] = currentTime.split(":").map(Number);
        const combinedDateTime = new Date(currentDate);
        combinedDateTime.setHours(hours, minutes, 0, 0);

        const isoString = formatToISOLocal(combinedDateTime, true);
        form.setValue(name, isoString);
      }
    } else {
      form.setValue(name, "");
    }
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!sideEffectsAllowed) return;
    handleDateTimeChange(selectedDate, undefined);
    if (selectedDate) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (newTime: string) => {
    if (!sideEffectsAllowed) return;
    handleDateTimeChange(undefined, newTime);
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem className="flex flex-col">
          <FormLabel>{label} {required && '*'}</FormLabel>
          <FormControl>
            <div className={cn("flex gap-4", dateOnly && "gap-0")}>
              <div className="flex flex-col gap-3">
                <Popover
                  open={isOpen}
                  onOpenChange={(open) => {
                    if (sideEffectsAllowed) setIsOpen(open);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      id={`${name}-date-picker`}
                      className={cn("w-32 justify-between font-normal", "disabled:bg-muted")}
                      disabled={!sideEffectsAllowed}
                    >
                      {date ? date.toLocaleDateString() : placeholder}
                      <ChevronDownIcon />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      captionLayout="dropdown"
                      onSelect={handleDateSelect}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {!dateOnly && (
                <div className="flex flex-col gap-3">
                  <Input
                    type="time"
                    id={`${name}-time-picker`}
                    value={time}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    disabled={!sideEffectsAllowed}
                    step="60"
                    className={cn(
                      "bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
                      "disabled:bg-muted"
                    )}
                  />
                </div>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
